import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { sendMetaLead } from "../../analytics/meta";
import {
  HUBSPOT_CONTACT_FORM_ID,
  sendLeadMail,
  submitHubSpotForm,
} from "../../api/hubspot";

// 無料相談・お問い合わせ。
// 送信先は HubSpot「問い合わせフォーム」。このフォームに無い項目（ご用件・部署・役職・きっかけ）は
// 送ると 400 になるので、message の先頭にまとめて入れる。
// 自動返信は WordPress 経由（kind=consult のテンプレート）。失敗してもフォーム自体は成立させる。
export default function ContactPage() {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    const form = e.currentTarget;
    setError("");
    if (!form.checkValidity()) {
      const bad = form.querySelector(":invalid");
      setError(
        bad?.name === "agree"
          ? "プライバシーポリシーへの同意が必要です。"
          : "必須項目を入力してください。",
      );
      bad?.focus();
      return;
    }
    const v = (n) => (form.elements[n]?.value || "").trim();
    const meta = [
      ["ご用件", v("inquiry")],
      ["部署", v("department")],
      ["役職", v("jobtitle")],
      ["LEAPSを知ったきっかけ", v("source")],
    ].filter(([, x]) => x);
    const message = `${meta.map(([k, x]) => `【${k}】${x}`).join("\n")}\n\n${v("message")}`;

    setSending(true);
    try {
      await submitHubSpotForm({
        formId: HUBSPOT_CONTACT_FORM_ID,
        pageName: "無料相談・お問い合わせ",
        fields: {
          lastname: v("lastname"),
          firstname: v("firstname"),
          company: v("company"),
          email: v("email"),
          phone: v("phone"),
          message,
        },
      });
      sendLeadMail("consult", {
        email: v("email"),
        name: `${v("lastname")} ${v("firstname")}`.trim(),
        company: v("company"),
        tel: v("phone"),
        message,
      });
      sendMetaLead({ content_name: "無料相談・お問い合わせ" });
      navigate("/contact/thanks");
    } catch (err) {
      console.warn("[contact] HubSpot submit failed", err);
      setError(
        "送信できませんでした。時間をおいて再度お試しいただくか、info@leaps-inc.com までご連絡ください。",
      );
      setSending(false);
    }
  }

  return (
    <main>
      <section className="section form-page">
        <div className="form-grid container">
          <div className="f-side">
            <p className="f-label">CONTACT</p>
            <h1 className="f-title">無料相談・お問い合わせ</h1>
            <p className="f-lead">
              LEAPSにご関心をお持ちいただき、ありがとうございます。
              <br />
              AI・ブロックチェーンの活用やサービス開発について、お気軽にご相談ください。
              <br />
              「まだ何も決まっていない」という段階でも構いません。
            </p>
            <div className="f-flow">
              <h2>お問い合わせ後の流れ</h2>
              <ol>
                <li>
                  <b>お問い合わせフォームの送信</b>
                  <span>このページのフォームからお送りください</span>
                </li>
                <li>
                  <b>担当者からご連絡</b>
                  <span>2〜3営業日以内に、メールでご連絡します</span>
                </li>
                <li>
                  <b>オンラインでご相談</b>
                  <span>
                    課題やご状況をうかがい、進め方をご提案します（無料）
                  </span>
                </li>
              </ol>
            </div>
            <p className="f-note">
              サービスの詳細は <Link to="/services">サービス一覧</Link>{" "}
              をご覧ください。
            </p>
          </div>
          <form
            className="f-card"
            id="contact-form"
            noValidate
            onSubmit={onSubmit}
          >
            <div className="f-field">
              <label htmlFor="cf-inquiry">
                ご用件<span className="req">必須</span>
              </label>
              <select id="cf-inquiry" name="inquiry" required>
                <option>無料相談（サービスのご相談）</option>
                <option>サービス・費用について</option>
                <option>取材・協業のご相談</option>
                <option>採用・その他</option>
              </select>
            </div>
            <div className="f-field">
              <label htmlFor="cf-company">
                会社名<span className="req">必須</span>
              </label>
              <input
                type="text"
                id="cf-company"
                name="company"
                placeholder="株式会社〇〇"
                required
                autoComplete="organization"
              />
            </div>
            <div className="f-row">
              <div className="f-field half">
                <label htmlFor="cf-lastname">
                  姓<span className="req">必須</span>
                </label>
                <input
                  type="text"
                  id="cf-lastname"
                  name="lastname"
                  placeholder="山田"
                  required
                  autoComplete="family-name"
                />
              </div>
              <div className="f-field half">
                <label htmlFor="cf-firstname">
                  名<span className="req">必須</span>
                </label>
                <input
                  type="text"
                  id="cf-firstname"
                  name="firstname"
                  placeholder="太郎"
                  required
                  autoComplete="given-name"
                />
              </div>
            </div>
            <div className="f-row">
              <div className="f-field half">
                <label htmlFor="cf-department">部署</label>
                <input
                  type="text"
                  id="cf-department"
                  name="department"
                  placeholder="経営企画部"
                />
              </div>
              <div className="f-field half">
                <label htmlFor="cf-jobtitle">役職</label>
                <input
                  type="text"
                  id="cf-jobtitle"
                  name="jobtitle"
                  placeholder="部長"
                  autoComplete="organization-title"
                />
              </div>
            </div>
            <div className="f-field">
              <label htmlFor="cf-email">
                メールアドレス<span className="req">必須</span>
              </label>
              <input
                type="email"
                id="cf-email"
                name="email"
                placeholder="sample@example.com"
                required
                autoComplete="email"
              />
            </div>
            <div className="f-field">
              <label htmlFor="cf-phone">電話番号</label>
              <input
                type="tel"
                id="cf-phone"
                name="phone"
                placeholder="03-0000-0000"
                autoComplete="tel"
              />
            </div>
            <div className="f-field">
              <label htmlFor="cf-source">LEAPSを知ったきっかけ</label>
              <select id="cf-source" name="source">
                <option value="">選択してください</option>
                <option>Web検索</option>
                <option>LEAPS AI Media の記事</option>
                <option>SNS</option>
                <option>ご紹介</option>
                <option>イベント・セミナー</option>
                <option>その他</option>
              </select>
            </div>
            <div className="f-field">
              <label htmlFor="cf-message">
                ご相談内容<span className="req">必須</span>
              </label>
              <textarea
                id="cf-message"
                name="message"
                required
                rows="6"
                placeholder="現在の課題や、ご検討中の内容をご記入ください。まだ何も決まっていない段階でも構いません。"
              />
            </div>
            <label className="f-agree">
              <input type="checkbox" name="agree" required />{" "}
              <span>
                <Link to="/privacy" target="_blank" rel="noopener">
                  プライバシーポリシー
                </Link>
                に同意する
              </span>
              <span className="req">必須</span>
            </label>
            <p className="f-error" role="alert" hidden={!error}>
              {error}
            </p>
            <button type="submit" className="btn f-submit" disabled={sending}>
              {sending ? "送信中…" : "送信する"}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}
